import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import allPhotoStoryPosts from "../data/photoStoryPosts";
import { buildApiUrl, buildPublicFileUrl } from "../config/api";
import "./PhotoStories.css";

const photoStoryPosts = allPhotoStoryPosts.filter(
  (post) => post.brand !== "Cloudy Pixel Studio"
);

const PAGE_TITLE = "Photo Stories & Wall Art Journal | MayaChitro";
const PAGE_DESCRIPTION =
  "Read MayaChitro photo stories, behind-the-frame journals, wall art ideas, buying guides, and licensing notes for photography downloads and printable wall art.";

const FALLBACK_IMAGE = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="1200" height="800" viewBox="0 0 1200 800">
    <rect width="1200" height="800" fill="#101719"/>
    <circle cx="600" cy="345" r="76" fill="none" stroke="#e4b01f" stroke-width="10" opacity="0.72"/>
    <path d="M557 356l31 31 58-75" fill="none" stroke="#e4b01f" stroke-width="12" stroke-linecap="round" stroke-linejoin="round" opacity="0.85"/>
    <text x="600" y="500" text-anchor="middle" fill="#fffaf0" font-family="Georgia, serif" font-size="52" font-weight="700">MayaChitro Journal</text>
    <text x="600" y="560" text-anchor="middle" fill="#bdb7a9" font-family="Arial, sans-serif" font-size="26" letter-spacing="6">VISUAL STORIES</text>
  </svg>
`)}`;

const toAssetUrl = (path) => {
  if (!path || typeof path !== "string") return FALLBACK_IMAGE;

  const cleanPath = path.trim().replace(/\\/g, "/");

  if (!cleanPath) return FALLBACK_IMAGE;
  if (/^(https?:)?\/\//i.test(cleanPath) || cleanPath.startsWith("data:")) {
    return cleanPath;
  }

  if (cleanPath.startsWith("/uploads") || cleanPath.startsWith("uploads/")) {
    return buildPublicFileUrl(cleanPath);
  }

  if (cleanPath.startsWith("images/")) return `/${cleanPath}`;

  return cleanPath;
};

const handleImageError = (event) => {
  event.currentTarget.onerror = null;
  event.currentTarget.src = FALLBACK_IMAGE;
  event.currentTarget.classList.add("is-fallback");
};

const setMetaTag = (selectorAttribute, key, content) => {
  if (!content) return;

  let tag = document.head.querySelector(`meta[${selectorAttribute}="${key}"]`);

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(selectorAttribute, key);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
};

const setCanonicalUrl = (url) => {
  let canonical = document.head.querySelector('link[rel="canonical"]');

  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }

  canonical.setAttribute("href", url);
};

const getField = (item, keys, fallback = "") => {
  for (const key of keys) {
    if (
      item?.[key] !== undefined &&
      item?.[key] !== null &&
      item?.[key] !== ""
    ) {
      return item[key];
    }
  }

  return fallback;
};

const getProductImage = (product) =>
  getField(product, [
    "coverImage",
    "cover_image",
    "thumbnailImage",
    "thumbnail_image",
    "thumbnail",
    "image",
    "previewImage",
    "preview_image",
  ]);

const getProductTitle = (product) =>
  getField(product, ["title", "name", "product_title"], "MayaChitro Product");

const getProductPrice = (product) =>
  Number(getField(product, ["price", "personalPrice", "personal_price"], 0));

const getProductSlug = (product) =>
  getField(product, ["slug", "productSlug", "product_slug"], "");

function StoryMeta({ post }) {
  return (
    <div className="mc-story-meta">
      <span>{post.type || "Photo Story"}</span>
      <span>{post.readingTime || "3 min read"}</span>
      <span>{post.date || "MayaChitro Journal"}</span>
    </div>
  );
}

function ProductMini({ product }) {
  const slug = getProductSlug(product);
  const image = toAssetUrl(getProductImage(product));
  const title = getProductTitle(product);
  const price = getProductPrice(product);

  if (!slug) return null;

  return (
    <Link className="mc-journal-product-mini" to={`/product/${slug}`}>
      <img src={image} alt={title} loading="lazy" decoding="async" onError={handleImageError} />

      <div>
        <strong>{title}</strong>
        <span>{price ? `$${price.toFixed(2)}` : "View product"}</span>
      </div>
    </Link>
  );
}

function StorySmallCard({ post }) {
  return (
    <article className="mc-story-small-card">
      <Link to={`/stories/${post.slug}`} className="mc-story-small-image">
        <img src={toAssetUrl(post.image)} alt={post.imageAlt || post.title} loading="lazy" decoding="async" onError={handleImageError} />
      </Link>

      <div>
        <p className="mc-journal-kicker">{post.category || post.type || "Story"}</p>

        <h3>
          <Link to={`/stories/${post.slug}`}>{post.title}</Link>
        </h3>

        <p>{post.excerpt}</p>

        <StoryMeta post={post} />
      </div>
    </article>
  );
}

function StoryTextRow({ post }) {
  return (
    <article className="mc-story-text-row">
      <div>
        <p className="mc-journal-kicker">{post.category || "Photo Story"}</p>

        <h3>
          <Link to={`/stories/${post.slug}`}>{post.title}</Link>
        </h3>

        <p>{post.excerpt}</p>

        <StoryMeta post={post} />
      </div>

      <Link to={`/stories/${post.slug}`} className="mc-story-row-image">
        <img src={toAssetUrl(post.image)} alt={post.imageAlt || post.title} loading="lazy" decoding="async" onError={handleImageError} />
      </Link>
    </article>
  );
}

function StoryColumnCard({ post, large = false }) {
  return (
    <article className={large ? "mc-story-column-card large" : "mc-story-column-card"}>
      <Link to={`/stories/${post.slug}`} className="mc-story-column-image">
        <img src={toAssetUrl(post.image)} alt={post.imageAlt || post.title} loading="lazy" decoding="async" onError={handleImageError} />
        <span>{post.type || "Photo Story"}</span>
      </Link>

      <div className="mc-story-column-copy">
        <p className="mc-journal-kicker">{post.category || "MayaChitro"}</p>

        <h3>
          <Link to={`/stories/${post.slug}`}>{post.title}</Link>
        </h3>

        <p>{post.excerpt}</p>

        <StoryMeta post={post} />

        <div className="mc-story-tags">
          {(post.tags || []).slice(0, 4).map((tag) => (
            <span key={tag}>{tag}</span>
          ))}
        </div>
      </div>
    </article>
  );
}

const PhotoStories = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "All";

  const [searchText, setSearchText] = useState("");
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  useEffect(() => {
    const pageUrl = `${window.location.origin}${window.location.pathname}`;
    const shareImage = toAssetUrl(photoStoryPosts[0]?.image);

    document.title = PAGE_TITLE;

    setMetaTag("name", "description", PAGE_DESCRIPTION);
    setMetaTag("name", "robots", "index, follow, max-image-preview:large");
    setMetaTag("property", "og:title", PAGE_TITLE);
    setMetaTag("property", "og:description", PAGE_DESCRIPTION);
    setMetaTag("property", "og:type", "website");
    setMetaTag("property", "og:url", pageUrl);
    setMetaTag("property", "og:image", shareImage);
    setMetaTag("name", "twitter:card", "summary_large_image");
    setMetaTag("name", "twitter:title", PAGE_TITLE);
    setMetaTag("name", "twitter:description", PAGE_DESCRIPTION);
    setMetaTag("name", "twitter:image", shareImage);
    setCanonicalUrl(pageUrl);

    const previousStructuredData = document.getElementById(
      "mc-photo-stories-structured-data"
    );

    if (previousStructuredData) previousStructuredData.remove();

    const structuredData = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Photo Stories & Wall Art Journal",
      description: PAGE_DESCRIPTION,
      url: pageUrl,
      isPartOf: {
        "@type": "WebSite",
        name: "MayaChitro",
        url: window.location.origin,
      },
      mainEntity: {
        "@type": "ItemList",
        itemListElement: photoStoryPosts.slice(0, 12).map((post, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `${window.location.origin}/stories/${post.slug}`,
          name: post.title,
        })),
      },
    };

    const script = document.createElement("script");
    script.id = "mc-photo-stories-structured-data";
    script.type = "application/ld+json";
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    let ignore = false;

    async function fetchProducts() {
      try {
        setLoadingProducts(true);

        const response = await fetch(buildApiUrl("/products?status=published"), {
          headers: { Accept: "application/json" },
        });

        const data = await response.json();

        if (!ignore && response.ok) {
          const loadedProducts = Array.isArray(data)
            ? data
            : data.products || data.data || [];

          setProducts(loadedProducts);
        }
      } catch (error) {
        console.error("Failed to load products for Photo Stories:", error);
      } finally {
        if (!ignore) setLoadingProducts(false);
      }
    }

    fetchProducts();

    return () => {
      ignore = true;
    };
  }, []);

  const categories = useMemo(() => {
    return [
      "All",
      ...Array.from(
        new Set(photoStoryPosts.map((post) => post.category).filter(Boolean))
      ),
    ];
  }, []);

  const filteredPosts = useMemo(() => {
    const term = searchText.trim().toLowerCase();

    return photoStoryPosts.filter((post) => {
      const categoryMatch =
        activeCategory === "All" || post.category === activeCategory;

      const searchableText = [
        post.title,
        post.excerpt,
        post.brand,
        post.category,
        post.type,
        ...(post.tags || []),
      ]
        .join(" ")
        .toLowerCase();

      const searchMatch = term ? searchableText.includes(term) : true;

      return categoryMatch && searchMatch;
    });
  }, [activeCategory, searchText]);

  const leadStory = filteredPosts[0] || photoStoryPosts[0];
  const topStories = filteredPosts.slice(1, 3);
  const latestStories = filteredPosts.slice(3, 9);
  const missedStories = filteredPosts.slice(9, 12);

  const behindFrameStories = filteredPosts.filter((post) => {
    const text = `${post.type || ""} ${post.category || ""} ${(post.tags || []).join(" ")}`.toLowerCase();

    return (
      text.includes("behind") ||
      text.includes("frame") ||
      text.includes("process") ||
      text.includes("video") ||
      text.includes("vlog")
    );
  });

  const guideStories = filteredPosts.filter((post) => {
    const text = `${post.type || ""} ${post.category || ""} ${(post.tags || []).join(" ")}`.toLowerCase();

    return (
      text.includes("guide") ||
      text.includes("licensing") ||
      text.includes("wall art") ||
      text.includes("decor") ||
      text.includes("buying")
    );
  });

  const featuredProducts = useMemo(() => {
    const storyProductSlugs = new Set(
      photoStoryPosts.map((post) => post.productSlug).filter(Boolean)
    );

    const exactProducts = products.filter((product) =>
      storyProductSlugs.has(product.slug)
    );

    const backupProducts = products.filter(
      (product) => !storyProductSlugs.has(product.slug)
    );

    return [...exactProducts, ...backupProducts].slice(0, 4);
  }, [products]);

  const changeCategory = (category) => {
    if (category === "All") {
      setSearchParams({});
    } else {
      setSearchParams({ category });
    }
  };

  return (
    <main className="mc-journal-page">
      <div className="mc-journal-edition-bar">
        <span>Independent visual journal</span>
        <strong>MayaChitro · Dhaka</strong>
        <span>{photoStoryPosts.length} published stories</span>
      </div>

      <section className="mc-journal-masthead">
        <div>
          <p className="mc-journal-kicker">MayaChitro Journal</p>
          <h1>Photo Stories</h1>
          <p>
            Visual essays, behind-the-frame notes, wall art ideas, and licensing
            guidance for buyers who want more than a download.
          </p>
        </div>

        <div className="mc-journal-masthead-links">
          <Link to="/shop?brand=mayachitro">Shop Photography</Link>
          <a href="mailto:MayaChitro@gmail.com?subject=Story%20Pitch%20for%20MayaChitro%20Journal">
            Pitch a Story
          </a>
        </div>
      </section>

      <section className="mc-journal-controls">
        <div className="mc-journal-category-tabs">
          {categories.map((category) => (
            <button
              type="button"
              key={category}
              className={activeCategory === category ? "active" : ""}
              onClick={() => changeCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        <input
          type="search"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder="Search stories, wall art, licensing..."
        />
      </section>

      {!filteredPosts.length && (
        <section className="mc-journal-empty">
          No stories found. Try another category or search term.
        </section>
      )}

      {leadStory && (
        <section className="mc-journal-frontpage">
          <article className="mc-lead-story">
            <Link to={`/stories/${leadStory.slug}`} className="mc-lead-image">
              <img
                src={toAssetUrl(leadStory.image)}
                alt={leadStory.imageAlt || leadStory.title}
                decoding="async"
                onError={handleImageError}
              />
            </Link>

            <div className="mc-lead-copy">
              <p className="mc-journal-kicker">
                {leadStory.category || "Lead Story"}
              </p>

              <h2>
                <Link to={`/stories/${leadStory.slug}`}>{leadStory.title}</Link>
              </h2>

              <p>{leadStory.excerpt}</p>

              <StoryMeta post={leadStory} />

              <div className="mc-lead-actions">
                <Link to={`/stories/${leadStory.slug}`}>Read the story →</Link>

                {leadStory.productSlug && (
                  <Link to={`/product/${leadStory.productSlug}`}>
                    View related product →
                  </Link>
                )}
              </div>
            </div>
          </article>

          <aside className="mc-top-story-stack">
            <div className="mc-section-label-row">
              <span>Top Stories</span>
              <small>{filteredPosts.length} stories</small>
            </div>

            {topStories.map((post) => (
              <StorySmallCard post={post} key={post.slug} />
            ))}
          </aside>
        </section>
      )}

      <section className="mc-journal-content-grid">
        <div className="mc-journal-main-column">
          <div className="mc-section-heading">
            <div>
              <p className="mc-journal-kicker">Latest Articles</p>
              <h2>Stories, buying guides, and quiet visual moments</h2>
            </div>
          </div>

          <div className="mc-newspaper-grid">
            {latestStories.map((post, index) => (
              <StoryColumnCard post={post} large={index === 0} key={post.slug} />
            ))}
          </div>

          {(behindFrameStories.length > 0 || guideStories.length > 0) && (
            <section className="mc-journal-section-split">
              <div>
                <div className="mc-section-heading small">
                  <div>
                    <p className="mc-journal-kicker">Process & Mood</p>
                    <h2>Behind the Frame</h2>
                  </div>
                </div>

                <div className="mc-story-row-list">
                  {(behindFrameStories.length ? behindFrameStories : filteredPosts.slice(0, 3))
                    .slice(0, 3)
                    .map((post) => (
                      <StoryTextRow post={post} key={`behind-${post.slug}`} />
                    ))}
                </div>
              </div>

              <div>
                <div className="mc-section-heading small">
                  <div>
                    <p className="mc-journal-kicker">Buyer Notes</p>
                    <h2>Wall Art & Licensing</h2>
                  </div>
                </div>

                <div className="mc-story-row-list">
                  {(guideStories.length ? guideStories : filteredPosts.slice(2, 5))
                    .slice(0, 3)
                    .map((post) => (
                      <StoryTextRow post={post} key={`guide-${post.slug}`} />
                    ))}
                </div>
              </div>
            </section>
          )}

          {missedStories.length > 0 && (
            <section className="mc-missed-section">
              <div className="mc-section-heading">
                <div>
                  <p className="mc-journal-kicker">In Case You Missed It</p>
                  <h2>More from the journal</h2>
                </div>
              </div>

              <div className="mc-missed-grid">
                {missedStories.map((post) => (
                  <StoryColumnCard post={post} key={`missed-${post.slug}`} />
                ))}
              </div>
            </section>
          )}
        </div>

        <aside className="mc-journal-sidebar">
          <div className="mc-sidebar-box">
            <p className="mc-journal-kicker">From the Shop</p>
            <h3>Featured Downloads</h3>
            <p>
              Connect each story with related photography downloads and printable
              wall art products.
            </p>

            {loadingProducts && (
              <div className="mc-journal-status">Loading products...</div>
            )}

            <div className="mc-journal-product-stack">
              {featuredProducts.map((product) => (
                <ProductMini
                  product={product}
                  key={product.id || product.productId || product.slug}
                />
              ))}
            </div>
          </div>

          <div className="mc-sidebar-box">
            <p className="mc-journal-kicker">Editorial Plan</p>
            <h3>Content That Can Sell</h3>

            <ul>
              <li>Story behind each photo</li>
              <li>Room styling ideas</li>
              <li>Photo licensing guidance</li>
              <li>Travel, culture, and river-life essays</li>
              <li>Internal links to product pages</li>
            </ul>
          </div>

          <div className="mc-sidebar-box accent">
            <p className="mc-journal-kicker">Newsletter</p>
            <h3>Get new stories and photo releases.</h3>
            <Link to="/#newsletter">Subscribe →</Link>
          </div>
        </aside>
      </section>

      <section className="mc-journal-archive" aria-labelledby="journal-archive-title">
        <div className="mc-section-heading">
          <div>
            <p className="mc-journal-kicker">The complete journal</p>
            <h2 id="journal-archive-title">Explore every MayaChitro story</h2>
          </div>
          <small>{filteredPosts.length} stories published</small>
        </div>
        <div className="mc-journal-archive-grid">
          {filteredPosts.map((post) => <StorySmallCard post={post} key={`archive-${post.slug}`} />)}
        </div>
      </section>
    </main>
  );
};

export default PhotoStories;
